import { getDB } from './db';
import { sniffImageMime, sha256Hex } from '../images/bytes';
import {
  AssetSchema,
  BackupSchema,
  DATA_VERSION,
  DesignSchema,
  NoteSchema,
  ScheduleSchema,
  SettingsSchema,
  SnapshotSchema,
  type Asset,
  type Backup,
  type Design,
  type Note,
  type Schedule,
  type Snapshot,
} from './schemas';
import { noteText } from '../notes/model';

export const MAX_IMPORT_BYTES = 50 * 1024 * 1024;

function toDataUrl(mime: string, data: ArrayBuffer): string {
  const bytes = new Uint8Array(data);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return `data:${mime};base64,${btoa(bin)}`;
}

function fromDataUrl(url: string): { mime: string; data: ArrayBuffer } | null {
  const m = /^data:([\w/+.-]+);base64,([A-Za-z0-9+/=\s]*)$/.exec(url);
  if (!m) return null;
  const bin = atob(m[2].replace(/\s/g, ''));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return { mime: m[1], data: out.buffer };
}

export async function exportBackup(opts: { includeHistory: boolean }): Promise<Backup> {
  const db = await getDB();
  const [notes, designs, schedules, assets, snapshots, settings, meta] = await Promise.all([
    db.getAll('notes'),
    db.getAll('designs'),
    db.getAll('schedules'),
    db.getAll('assets'),
    opts.includeHistory ? db.getAll('snapshots') : Promise.resolve([]),
    db.get('meta', 'settings'),
    db.get('meta', 'meta'),
  ]);
  return {
    app: 'memonest',
    dataVersion: DATA_VERSION,
    exportedAt: Date.now(),
    notes,
    designs,
    schedules,
    snapshots: opts.includeHistory ? snapshots : undefined,
    assets: assets.map((a) => ({ id: a.id, kind: a.kind, mime: a.mime, name: a.name, width: a.width, height: a.height, dataUrl: toDataUrl(a.mime, a.data) })),
    settings,
    meta,
  };
}

export interface ImportReport {
  notes: number;
  designs: number;
  schedules: number;
  assets: number;
  snapshots: number;
  skipped: string[];
}

/**
 * Validate and merge a backup. Existing records with the same id are replaced only when the imported copy is newer.
 * Nothing is written unless the file as a whole parses.
 */
export async function importBackup(text: string): Promise<ImportReport> {
  if (text.length > MAX_IMPORT_BYTES * 1.4) throw new Error('This file is too large to import (limit 50 MB).');
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error('This is not a MemoNest backup (the file is not valid JSON).');
  }
  const parsed = BackupSchema.safeParse(json);
  if (!parsed.success) throw new Error('This is not a MemoNest backup, or it was made by a newer version.');
  const b = parsed.data;
  const report: ImportReport = { notes: 0, designs: 0, schedules: 0, assets: 0, snapshots: 0, skipped: [] };

  const notes: Note[] = [];
  for (const raw of b.notes) {
    const r = NoteSchema.safeParse(raw);
    if (r.success) notes.push(r.data);
    else report.skipped.push(`note ${(raw as { id?: string })?.id ?? '?'}: ${r.error.issues[0]?.message}`);
  }
  const designs: Design[] = [];
  for (const raw of b.designs) {
    const r = DesignSchema.safeParse(raw);
    if (r.success && r.data.source === 'custom') designs.push(r.data);
    else report.skipped.push(`design ${(raw as { id?: string })?.id ?? '?'}`);
  }
  const schedules: Schedule[] = [];
  for (const raw of b.schedules) {
    const r = ScheduleSchema.safeParse(raw);
    if (r.success) schedules.push(r.data);
    else report.skipped.push(`schedule ${(raw as { id?: string })?.id ?? '?'}`);
  }
  const snapshots: Snapshot[] = [];
  for (const raw of b.snapshots ?? []) {
    const r = SnapshotSchema.safeParse(raw);
    if (r.success) snapshots.push(r.data);
  }
  const assets: Asset[] = [];
  for (const a of b.assets) {
    const decoded = fromDataUrl(a.dataUrl);
    if (!decoded) {
      report.skipped.push(`asset ${a.id}: unreadable data`);
      continue;
    }
    if (a.kind === 'image') {
      const sniffed = sniffImageMime(new Uint8Array(decoded.data));
      if (!sniffed) {
        report.skipped.push(`asset ${a.id}: not a supported image`);
        continue;
      }
      decoded.mime = sniffed;
    }
    const asset = AssetSchema.safeParse({
      id: a.id,
      kind: a.kind,
      mime: decoded.mime,
      name: a.name,
      size: decoded.data.byteLength,
      width: a.width,
      height: a.height,
      hash: await sha256Hex(decoded.data),
      createdAt: Date.now(),
      data: decoded.data,
    });
    if (asset.success) assets.push(asset.data);
  }

  const db = await getDB();
  const tx = db.transaction(['notes', 'designs', 'schedules', 'assets', 'snapshots', 'meta'], 'readwrite');
  for (const a of assets) {
    await tx.objectStore('assets').put(a);
    report.assets++;
  }
  for (const n of notes) {
    const existing = await tx.objectStore('notes').get(n.id);
    if (existing && existing.updatedAt >= n.updatedAt) continue;
    if (existing) {
      // Keep the version being replaced in history.
      await tx.objectStore('snapshots').put({
        id: `imp-${n.id}-${Date.now()}`,
        noteId: n.id,
        createdAt: Date.now(),
        reason: 'import',
        label: 'Before import',
        text: noteText(existing),
        chars: noteText(existing).length,
        note: existing,
      });
    }
    await tx.objectStore('notes').put({ ...n, rev: (existing?.rev ?? 0) + 1 });
    report.notes++;
  }
  for (const d of designs) {
    await tx.objectStore('designs').put(d);
    report.designs++;
  }
  for (const s of schedules) {
    await tx.objectStore('schedules').put(s);
    report.schedules++;
  }
  for (const s of snapshots) {
    await tx.objectStore('snapshots').put(s);
    report.snapshots++;
  }
  const settings = SettingsSchema.safeParse(b.settings);
  if (settings.success && !(await tx.objectStore('meta').get('settings'))) await tx.objectStore('meta').put(settings.data, 'settings');
  await tx.done;
  return report;
}

/** Markdown export of one or many notes (text + checklist; images and drawings are noted, not embedded). */
export function notesToMarkdown(notes: Note[]): string {
  return notes
    .map((n) => {
      const title = n.title.trim() || n.lines.find((l) => l.text.trim())?.text.trim() || 'Untitled';
      const body = n.lines
        .map((l) => {
          const t = l.marks?.heading ? `### ${l.text}` : l.text;
          return l.kind === 'check' ? `- [${l.checked ? 'x' : ' '}] ${l.text}` : t;
        })
        .join('\n');
      const extras = [n.images.length ? `_${n.images.length} image(s)_` : '', n.strokes.length ? '_contains a drawing_' : ''].filter(Boolean).join(' · ');
      const tags = n.tags.length ? `\n\nTags: ${n.tags.map((t) => `#${t}`).join(' ')}` : '';
      return `## ${title}\n\n${body}${extras ? `\n\n${extras}` : ''}${tags}\n\n<sub>Updated ${new Date(n.updatedAt).toISOString()}</sub>`;
    })
    .join('\n\n---\n\n');
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
